import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Download } from "lucide-react";
import { formatDateTimeFr } from "@/lib/format";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/documents"); setDocs(data.items || []); }
    catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document envoyé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    }
    setUploading(false);
    e.target.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Document supprimé"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Coffre-fort"
        title="Documents"
        description="Baux, factures, actes, quittances… Stockage privé, chiffré et isolé par organisation."
        actions={
          <>
            <input ref={fileInput} type="file" className="hidden" onChange={upload}
                   accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.csv,.txt" />
            <Button className="btn-pill" data-testid={TID.documentsUploadBtn}
                    onClick={() => fileInput.current?.click()} disabled={uploading}>
              <Upload className="mr-2 h-4 w-4" /> {uploading ? "Envoi…" : "Ajouter un document"}
            </Button>
          </>
        }
      />

      <div data-testid={TID.documentsList} className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-4">Nom du document</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4">Taille</th>
              <th className="px-6 py-4">Ajouté le</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && docs.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-16 text-center text-muted-foreground">
                <FileText className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" />
                Aucun document. Envoyez votre premier fichier.
              </td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="hover:bg-secondary/30">
                <td className="px-6 py-4 flex items-center gap-2 font-medium">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate max-w-[320px]">{d.original_filename}</span>
                </td>
                <td className="px-6 py-4 text-xs font-mono text-muted-foreground">{d.content_type}</td>
                <td className="px-6 py-4 text-muted-foreground">{Math.round((d.size || 0) / 1024)} Ko</td>
                <td className="px-6 py-4 text-muted-foreground">{formatDateTimeFr(d.created_at)}</td>
                <td className="px-6 py-4 text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
